
import { useState } from "react";

import { FilledButton, OutlinedButton } from "../../common/Button";


import useCurrentUser from "../../../hooks/useCurrentUser";
import { FreelancerRole, FreelancerSkillExperience, FreelancerToolExperience, IFreelancer } from "../../../data_models/freelancer";
import Lightbox from "../../common/Lightbox";
import TextInputField from "../../common/TextInputField";
import TextInputArea from "../../common/TextInputArea";
import { OutlinedDropdownMenu } from "../../common/DropdownMenu";
import MultiTextSelect from "../../common/MultiTextSelect";
import { OutlinedMultiDropdownMenu } from "../../common/MultiDropdownMenu";
import { getNumericEnumValues, getStringEnumValues } from "../../../utils/enumUtils";
import { UserEnumTextMapper } from "../../../data_models/enumToTextMapper";
import EditSkillExperience from "../../profile/common/EditSkillExperience";
import { EmployerType, IEmployer } from "../../../data_models/employer";
import { set } from "react-datepicker/dist/date_utils";
import { ECountry, countriesToTextMapper } from "../../../data_models/data/countries";


interface EditBasicCompanyInfoLightboxProps {
    onClose: () => void;
}

export const EditBasicCompanyInfoLightbox: React.FC<EditBasicCompanyInfoLightboxProps> = ({ onClose }) => {

    const { currentUser, updateCurrentUser } = useCurrentUser();
    const [loading, setLoading] = useState(false);


    const [employerName, setEmployerName] = useState((currentUser as IEmployer)?.employerName || '');
    const [employerType, setEmployerType] = useState<EmployerType>((currentUser as IEmployer)?.employerType || EmployerType.COMPANY);
    const [languages, setLanguages] = useState((currentUser as IEmployer)?.languages || []);
    const [phone, setPhone] = useState((currentUser as IEmployer)?.phone || '');

    const [streetAddress, setStreetAddress] = useState((currentUser as IEmployer)?.streetAddress || '');
    const [streetAddress2, setStreetAddress2] = useState((currentUser as IEmployer)?.streetAddress2 || '');
    const [city, setCity] = useState((currentUser as IEmployer)?.city || '');
    const [state, setState] = useState((currentUser as IEmployer)?.state || '');
    const [zipCode, setZipCode] = useState((currentUser as IEmployer)?.zipCode || '');
    const [country, setCountry] = useState<ECountry>((currentUser as IEmployer)?.country || ECountry.UNITED_STATES);

    const onSubmit = async () => {
        setLoading(true);
        try {

            const updateObj: IEmployer = {
                employerName,
                employerType,
                languages,
                phone,
                streetAddress,
                streetAddress2,
                city,
                state,
                zipCode,
                country,
            }   

            await updateCurrentUser(updateObj);
        } catch (error: any) {
            console.error(error);
        } finally {
            setLoading(false);
            onClose();
        }
    }

    return (
        <Lightbox
            content={
                <div className="flex flex-col space-y-6 w-full" >
                    <h4>Update Basic Information</h4>


                    {/* Employer Name */}
                    <TextInputField
                        type="text"
                        id="employerName"
                        label="Name"
                        placeholder="Name"
                        value={employerName}
                        onChange={(e) => setEmployerName(e.target.value)}
                    />

                    {/* Type */}
                    <OutlinedDropdownMenu
                        options={getNumericEnumValues(EmployerType)}
                        optionsToTextMapper={UserEnumTextMapper.employerType}
                        onSelect={(value) => {
                            const val = value as unknown as EmployerType;
                            setEmployerType(val)
                        }}
                        defaultValue={(currentUser as IEmployer)?.employerType || EmployerType.COMPANY}
                        label="Type"
                    />

                    <MultiTextSelect
                        elements={languages}
                        setElements={setLanguages}
                        label="Spoken Languages"
                    />

                    <div className="flex flex-col space-y-6 p-2 w-full bg-neutral-100 rounded-xl">
                        <p className="caption2 ">The following fields are only for legal or technical purposes and won't be shared.</p>
                        {/* Phone */}
                        <TextInputField
                            type="text"
                            id="phone"
                            label="Phone"
                            placeholder="Phone Number"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />


                        <hr className="border-neutral-300 mx-10" />

                        {/* Street Address */}
                        <TextInputField
                            type="text"
                            id="streetAddress"
                            label="Street Address"
                            placeholder="Street Address"
                            value={streetAddress}
                            onChange={(e) => setStreetAddress(e.target.value)}
                        />

                        {/* Street Address 2 */}
                        <TextInputField
                            type="text"
                            id="streetAddress2"
                            label="Street Address 2 (optional)"
                            placeholder="Apt, Unit, Floor, Suite, etc."
                            value={streetAddress2}
                            onChange={(e) => setStreetAddress2(e.target.value)}
                        />

                        {/* City */}
                        <TextInputField
                            type="text"
                            id="city"
                            label="City"
                            placeholder="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />

                        {/* State */}
                        <TextInputField
                            type="text"
                            id="state"
                            label="State"
                            placeholder="State"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                        />

                        {/* Zipcode */}
                        <TextInputField
                            type="text"
                            id="zipCode"
                            label="Zip Code"
                            placeholder="Zip Code"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                        />

                        {/* Country */}
                        <OutlinedDropdownMenu
                            options={getStringEnumValues(ECountry)}
                            optionsToTextMapper={countriesToTextMapper}
                            onSelect={(value) => {
                                const val = value as unknown as ECountry;
                                setCountry(val)
                            }}
                            defaultValue={((currentUser as IEmployer).country || ECountry.UNITED_STATES)}
                            label="Country"
                        />
                    </div>

                    <div className="flex flex-row justify-end w-full space-x-2">
                        <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
                        <FilledButton
                            isLoading={loading}
                            onClick={onSubmit}>Submit</FilledButton>
                    </div>
                </div>}
            onClose={onClose}
        />
    );
}

